import { useEffect, useState } from 'react';
import CustomModalOverlay from '../../../../common/Custom/CustomModalOverlay/CustomModalOverlay';
import GlobalButton from '../../../../ui/GlobalButton';
import TextInput from '../../../../ui/TextInput/TextInput';
import TextArea from '../../../../ui/TextArea/TextArea';
import { useForm } from '@mantine/form';
import IntakeService from '../../../../../services/IntakeService';
import { getErrorMessage } from '../../../../../utils/helpers/helpers';
import SelectInput from '../../../../ui/SelectInput/SelectInput';
import {
  Amplitude,
  Confluence,
  Figma,
  Github,
  GoogleDocs,
  GoogleDrive,
  Notion,
  OtherIcon,
  Slack,
} from '../../../../../svgs/logos';
import { useLinkValidation } from '../../useLinkValidation';
import type { Intake, AddIntakeLinkPayload } from '@/types';

interface AddIntakeLinkModalProps {
  open: boolean;
  close: () => void;
  intake: Intake | null;
  refetch: () => Promise<void>;
}

export const LINK_TYPE_OPTIONS = [
  { label: 'Google Docs', value: 'Google Docs', icon: <GoogleDocs /> },
  { label: 'Google Drive', value: 'Google Drive', icon: <GoogleDrive /> },
  { label: 'Confluence', value: 'Confluence', icon: <Confluence /> },
  { label: 'Notion', value: 'Notion', icon: <Notion /> },
  { label: 'Figma', value: 'Figma', icon: <Figma /> },
  { label: 'Github', value: 'GitHub', icon: <Github /> },
  { label: 'Amplitude', value: 'Amplitude', icon: <Amplitude /> },
  { label: 'Slack', value: 'Slack', icon: <Slack /> },
  { label: 'Other', value: 'Other', icon: <OtherIcon size={16} /> },
];

export default function AddIntakeLinkModal({
  open,
  close,
  intake,
  refetch,
}: AddIntakeLinkModalProps) {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [shaking, setShaking] = useState(false);

  

  const triggerShake = () => {
    setShaking(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setShaking(true));
    });
  };
  const form = useForm({
    initialValues: {
      intake_link_name: '',
      intake_link_url: '',
      intake_link_type: '',
      intake_link_description: '',
    },
  });

  const { detectedType } = useLinkValidation(form.values.intake_link_url);

  useEffect(() => {
    if (detectedType) {
      form.setFieldValue('intake_link_type', detectedType);
    }
  }, [detectedType]);

  const handleSubmit = async () => {
    if (!intake?._id) {
      return;
    }

    if (
      !form.values.intake_link_url ||
      !form.values.intake_link_type ||
      !form.values.intake_link_name
    ) {
      return;
    }

    setLoading(true);
    setErrorMessage('');
    try {
      const payload: AddIntakeLinkPayload = {
        intake_link_url: form.values.intake_link_url,
        intake_link_type: form.values.intake_link_type as any,
        intake_link_name: form.values.intake_link_name,
        intake_link_description: form.values.intake_link_description,
      };

      await IntakeService.addIntakeLink(intake._id, payload);
      await refetch();
      form.reset();
      close();
    } catch (error) {
      console.error('Error adding link:', error);
      setErrorMessage(getErrorMessage(error));
      triggerShake();
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setErrorMessage('');
    close();
    form.reset();
  };

  return (
    <CustomModalOverlay
      open={open}
      onClose={handleClose}
      title={'Add Link to Intake'}
      subTitle={
        'Add a link that helps others understand or follow this intake easily. Collaboration works best when information is shared.'
      }
      width="600px"
    >
      <div>
        <form className="flex flex-col gap-4">
          <TextInput
            label={'Link Name'}
            placeholder={'e.g., Regression report'}
            {...form.getInputProps('intake_link_name')}
          />

          <TextInput
            label={'Enter URL'}
            placeholder={'Enter a valid url'}
            {...form.getInputProps('intake_link_url')}
          />

          <SelectInput
            label={'Link Type'}
            placeholder="Select the link type"
            {...form.getInputProps('intake_link_type')}
            options={LINK_TYPE_OPTIONS}
          />

          <TextArea
            label={'Description'}
            placeholder={'Add a brief description of this link'}
            height="80px"
            {...form.getInputProps('intake_link_description')}
          />
        </form>

        {errorMessage && (
          <p
            className={`mt-2 text-sm text-error-2 ${shaking ? 'shake' : ''}`}
            onAnimationEnd={() => setShaking(false)}
          >
            {errorMessage}
          </p>
        )}

        <div className="mt-6 mb-4 flex items-center justify-end gap-4">
          <div>
            <GlobalButton
              variant="stroke"
              onClick={handleClose}
            >
              Cancel
            </GlobalButton>
          </div>
          <div>
            <GlobalButton
              onClick={handleSubmit}
              type={'submit'}
              state={loading ? 'inactive' : 'default'}
            >
              Save Link
            </GlobalButton>
          </div>
        </div>
      </div>
    </CustomModalOverlay>
  );
}
